import React, { useEffect, useState } from "react";
import { useTheme } from "@material-ui/core/";
import MuiTable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableContainer from "@material-ui/core/TableContainer";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import { Column, useTable } from "react-table";
import Typography from "@material-ui/core/Typography";
import { useStressEntryContext } from "./StressEntryContext";
import { STRESSOR_CHECK_IN_MODE } from "./constants";
import { useUnitCheckerColumns } from "./unitCheckerColumns";
import { StressExposure } from "./StressExposure";
import { DampHeatFields } from "./DampHeatFields";
import { CidFields } from "./CidFields";
import { PidFields } from "./PidFields";
import { ThermalCycleFields } from "./ThermalCycleFields";
import { HumidityFreezeFields } from "./HumidityFreezeFields";
import { UVIDFields } from "./UVIDFields";
import { DMLFields } from "./DMLFields";
import { LetidFields } from "./LetidFields";
import { HailFields } from "./HailFields";
import { SMLFields } from "./SMLFields";

export const UnitChecker = () => {
  const theme = useTheme();
  const { state } = useStressEntryContext();
  const [localSerialNumber, setLocalSerialNumber] = useState("");

  useEffect(() => {
    if (state.submittedSerialNumber) {
      setLocalSerialNumber(state.submittedSerialNumber);
    }
  }, [state.submittedSerialNumber]);

  const getUnitVersion = state.unitVersion;

  const {
    ID,
    DELETE,
    METADATA,
    MODEL,
    NAME,
    PROCEDURE_TYPE,
    SERIAL_NUMBER,
    LOW_CURRENT,
    APPLY_CURRENT,
  } = useUnitCheckerColumns(getUnitVersion);

  const stressorColumns = React.useMemo(() => {
    const baseColumns = [ID, SERIAL_NUMBER, MODEL];
    const checkInExtraColumns = [PROCEDURE_TYPE, NAME, METADATA];
    const commonEndColumns = [DELETE];

    const conditionalColumns = [];

    if (state.isThermalCyclingCheck) {
      conditionalColumns.push(LOW_CURRENT);
    }

    if (state.isHumidityTestCheck) {
      conditionalColumns.push(LOW_CURRENT);
    }

    if (state.isCidTestCheck) {
      conditionalColumns.push(APPLY_CURRENT);
    }

    if (state.mode === STRESSOR_CHECK_IN_MODE) {
      return [
        ...baseColumns,
        ...checkInExtraColumns,
        ...conditionalColumns,
        ...commonEndColumns,
      ];
    }

    return [...baseColumns, NAME, METADATA, ...commonEndColumns];
  }, [
    state.mode,
    state.isThermalCyclingCheck,
    state.isHumidityTestCheck,
    state.isCidTestCheck,
    state.isLetidTestCheck,
    state.checkedUnitsTable,
    getUnitVersion,
  ]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable({
      data: state.checkedUnitsTable,
      initialState: {
        hiddenColumns: ["id"],
      },
      columns: stressorColumns as Column<any>[],
    });

  if (!state.checkedUnitsTable.length) {
    return null;
  }

  const getUniqueUnits = (units: any) =>
    units.filter(
      (unit: any, index: any, self: any) =>
        index ===
        self.findIndex((item: any) => item.serialNumber === unit.serialNumber),
    );

  const uniqueUnitCount = getUniqueUnits(state.checkedUnitsTable).length;

  return (
    <>
      <Typography variant="h5">
        {state.mode === STRESSOR_CHECK_IN_MODE
          ? `Checked in units:${state.checkedUnitsTable.length}`
          : `Checked out units:${uniqueUnitCount}`}
      </Typography>
      <Paper style={{ marginTop: 32 }}>
        <TableContainer
          data-testid="tableContainer"
          style={theme.tableContainer}
        >
          <MuiTable stickyHeader {...getTableProps()}>
            <TableHead>
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableCell {...column.getHeaderProps()}>
                      <Grid
                        container
                        direction="row"
                        justify="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Grid item xs={12} {...column.getHeaderProps()}>
                          <Typography variant="h6">
                            {column.render("Header")}
                          </Typography>
                        </Grid>
                      </Grid>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <TableCell {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </Paper>
      <DampHeatFields UnitVersion={getUnitVersion?.version} />
      <StressExposure UnitVersion={getUnitVersion?.version} />
      <CidFields />
      <PidFields />
      <ThermalCycleFields UnitVersion={getUnitVersion?.version} />
      <HumidityFreezeFields />
      <UVIDFields UnitVersion={getUnitVersion?.version} />
      <DMLFields />
      <LetidFields />
      <HailFields
        serialnumber={localSerialNumber}
        UnitVersion={getUnitVersion?.version}
      />
      <SMLFields UnitVersion={getUnitVersion} />
    </>
  );
};
